import { Component, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { WorkspaceOption } from '../workspace-option/workspace-option';
import { WorkspaceAccesible } from '../../core/services/workspace-access.service';

export interface DatosCambioWorkspace {
  /**
   * La lista, o la promesa de la lista.
   *
   * Se acepta la promesa para que el diálogo abra apenas se hace clic, en vez
   * de que quien lo abre espere la respuesta con la pantalla quieta. El
   * cargando vive adentro, que es donde el usuario lo está mirando.
   */
  workspaces: WorkspaceAccesible[] | Promise<WorkspaceAccesible[]>;
  activoId: string | null;
}

/**
 * Cambio de workspace ya estando dentro. Se elige y se confirma en dos pasos:
 * cambiar de workspace recarga el rol, las sedes y los permisos, así que no
 * conviene que ocurra por un clic accidental en una lista.
 */
@Component({
  selector: 'app-switch-workspace-modal',
  imports: [MatDialogModule, MatProgressSpinnerModule, TranslateModule, WorkspaceOption],
  templateUrl: './switch-workspace-modal.html',
  styleUrl: './switch-workspace-modal.scss',
})
export class SwitchWorkspaceModal {
  private dialogRef = inject(MatDialogRef<SwitchWorkspaceModal>);
  private datos = inject<DatosCambioWorkspace>(MAT_DIALOG_DATA);

  protected readonly workspaces = signal<WorkspaceAccesible[]>([]);
  protected readonly cargando = signal(true);
  protected readonly activoId = this.datos.activoId;
  protected readonly seleccionadoId = signal<string | null>(this.datos.activoId);

  constructor() {
    // `Promise.resolve` toma por igual la lista ya resuelta y la promesa, así
    // que quien abre el diálogo puede pasar cualquiera de las dos.
    Promise.resolve(this.datos.workspaces)
      .then(lista => this.workspaces.set(lista ?? []))
      .catch(() => this.workspaces.set([]))
      .finally(() => this.cargando.set(false));
  }

  protected estaSeleccionado(ws: WorkspaceAccesible): boolean {
    return ws.id === this.seleccionadoId();
  }

  protected esElActivo(ws: WorkspaceAccesible): boolean {
    return ws.id === this.activoId;
  }

  protected elegir(ws: WorkspaceAccesible): void {
    this.seleccionadoId.set(ws.id);
  }

  protected cancelar(): void {
    this.dialogRef.close();
  }

  protected continuar(): void {
    const elegido = this.workspaces().find(w => w.id === this.seleccionadoId()) ?? null;
    // Elegir el que ya está activo no es un cambio: se cierra sin hacer nada.
    this.dialogRef.close(elegido && elegido.id !== this.activoId ? elegido : null);
  }
}
